import { Component, forwardRef, Input, OnDestroy, OnInit } from '@angular/core';
import { ControlValueAccessor, FormControl, FormGroup, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Subscription } from 'rxjs';
import { ISelectedVoteOption } from '../../../models/ballot-base/proxy-vote/selected-vote-option';
import { IVoteOption } from '../../../../../../shared/models/ballot-vote';


@Component({
    selector: 'app-select-proxy-vote-option',
    templateUrl: './select-proxy-vote-option.component.html',
    styleUrl: './select-proxy-vote-option.component.scss',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => SelectProxyVoteOptionComponent),
            multi: true,
        }
    ],
    standalone: false
})
export class SelectProxyVoteOptionComponent<TVoteOptionId> implements OnInit, OnDestroy, ControlValueAccessor {
  @Input() selectedVoteOptionId: TVoteOptionId | null = null;
  @Input() voteOptions: { [key: string]: IVoteOption } = {};
  mod3: number = 0;
  voteOptionKeys: string[] = [];
  isDisabled: boolean = false;

  voteOptionForm!: FormGroup;
  subscriptions: Subscription = new Subscription();

  ngOnInit(): void {

    this.voteOptionKeys = Object.keys(this.voteOptions);
    this.mod3 = this.voteOptionKeys.length % 3;

    this.voteOptionForm = this.convertVoteOptionsToFormGroup(this.voteOptions);
    this.subscribeToFormChanges();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  onChange: (value: ISelectedVoteOption<TVoteOptionId> | null) => void = () => { };
  onTouched: any = () => { };

  writeValue(newValue: Partial<ISelectedVoteOption<TVoteOptionId>>): void {

    if (!newValue || !!Object.hasOwn(newValue, 'voteOptionId') || !newValue.voteOptionId) {
      return;
    }

    const formControlKey = newValue.voteOptionId as unknown as string;
    const formControl : FormControl = this.voteOptionForm?.get(formControlKey) as FormControl;

    if (formControl) {
      formControl.patchValue(newValue, { emitEvent: false }); //Set value of the VoteOption and comment.

      if (newValue.isChecked) {
        this.setVoteOption(formControlKey, newValue.isChecked); // Deselect other vote options
      }
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  convertVoteOptionsToFormGroup(voteOptions: { [key: string]: IVoteOption }): FormGroup {
    let group: {[key:string]: FormControl} = {};

    for (const key in voteOptions) {
      group[key] = new FormControl();
    }

    return new FormGroup(group);
  }

  subscribeToFormChanges(): void {
    if (this.voteOptionForm) {
      // Subscribe to individual controls so it is possible to know which control was changed.
      for (const key in this.voteOptionForm.controls) {
        const subscription = this.voteOptionForm.get(key)?.valueChanges.subscribe((formValue: ISelectedVoteOption<TVoteOptionId>) => {
          if(formValue.isChecked) {
            // Turn off other vote options if this one is checked.
            this.setVoteOption(key, formValue.isChecked);
            this.onChange(formValue);
          } else {
            // No need to do anything extra if user is unchecking option.
            this.onChange(null);
          }
        });

        if (subscription) {
          this.subscriptions.add(subscription);
        }
      }
    }
  }

  setVoteOption(voteOptionId: string, isChecked: boolean) {
    const formControl: FormControl =  this.voteOptionForm?.get(voteOptionId) as FormControl;

    if (formControl) {
      formControl.patchValue({isChecked: isChecked}, { emitEvent: false });

      // Turn off other vote options if this one is checked.
      if (isChecked) {
        for (const key in this.voteOptionForm.controls) {
          if (key !== voteOptionId) {
            this.deselectVoteOption(key);
          }
        }
      }
    }
  }

  deselectVoteOption(formControlKey: string) {
    const formControl: FormControl =  this.voteOptionForm?.get(formControlKey) as FormControl;

    if (formControl) {
      formControl.patchValue({ isChecked: false }, { emitEvent: false });
    }
  }
}

